import { LightObject, LightModel, BookingDB } from "./BookingLightDB"
import { computed, action, observable } from "mobx"
import { uuid } from "../../light/utils"
import { Session } from "./Session"
import { Member, $Member } from "./Member"
import dayjs = require("dayjs")
import { SessionType } from "./SessionType"
import { Waiver } from "./Waiver"
import { Order } from "./Order"
import { SessionGroup } from "./SessionGroup"
import { lightDBHost } from "../../light/utils"

export interface $Booking extends LightObject {
  arrivingDateString: string
  $memberOwner?: string
  $members: { [$id: string]: string }
  $orders: { [$id: string]: string }
}

export class Booking implements LightModel {
  constructor(db: BookingDB, object?: $Booking) {
    this.db = db
    this.object = object ? object : Booking.create$Booking()
    this.currentOrder = new Order(
      this.db,
      this.id,
      "booking",
      this.sqLocationId
    )
  }

  @computed
  get id() {
    return this.object.$id
  }

  @computed
  get type() {
    return this.object.$type
  }

  @computed
  get arrivingDate() {
    return dayjs(this.object.arrivingDateString)
  }

  db: BookingDB

  @computed
  get children(): LightModel[] {
    let children: LightModel[] = []
    children = children.concat(this.members)
    children.push(this.currentOrder)
    return children
  }

  isValid = true
  invalidMessage = undefined
  clean() {}

  static create$Booking(): $Booking {
    return {
      $id: uuid(),
      $type: "booking",
      $version: uuid(),
      $parentVersion: null,
      $versionDateString: new Date().toISOString(),
      $members: {},
      $orders: {},
      arrivingDateString: new Date().toISOString()
    }
  }

  setMemberOwner(member: Member) {
    this.currentOrder.object.$member = member.id
    this.object.$memberOwner = member.id
  }

  @observable
  object: $Booking

  @computed
  get members(): Member[] {
    if (this.object.$members) {
      return Object.keys(this.object.$members).map(k => {
        const object = this.db.file.objects[k]
        return new Member(this.db, object as $Member)
      })
    }
    return []
  }

  @computed
  get activityPlan(): { member: Member; sessionGroup: SessionGroup }[] {
    const planMap: {
      [id: string]: { member: Member; sessionGroup: SessionGroup }[]
    } = {}
    this.currentOrder.sessionGroups.forEach(group => {
      group.members.forEach(m => {
        let activities = planMap[m.id]
        if (!activities) {
          activities = []
          planMap[m.id] = activities
        }
        activities.push({ member: m, sessionGroup: group })
      })
    })
    const res: { member: Member; sessionGroup: SessionGroup }[] = []
    Object.values(planMap).forEach(v => {
      v.forEach(v1 => {
        res.push(v1)
      })
    })
    return res
  }

  @computed
  get allAdults(): Member[] {
    return this.members.filter(m => !m.isMinor)
  }

  @computed
  get allMinors(): Member[] {
    return this.members.filter(m => m.isMinor)
  }

  nextAvailableSession(sessionType: SessionType): Session | undefined {
    const sessions = sessionType.sessionsForDay(
      dayjs(this.object.arrivingDateString)
    )
    return sessions.find(this.isSessionAvailable.bind(this))
  }

  isSessionAvailable(session: Session): boolean {
    return (
      session.capacityLeft > 0 &&
      session.isInFuture &&
      this.availableMembersForSession(session).length > 0
    )
  }

  availableMembersForSession(session: Session): Member[] {
    const timeSlotMinusThirty = session.date
      .add(-30, "minute")
      .format(Session.timeSlotFormat)

    return this.members.filter(m => {
      const ans = this.currentOrder.sessionGroups.find(s => {
        const ans =
          s.object.$members[m.id] != null &&
          (s.session.timeSlot === session.timeSlot ||
            s.session.timeSlot === timeSlotMinusThirty)
        return session.sessionType.object.name.toLowerCase() != "play mo"
          ? ans && s.session.sessionType.object.name.toLowerCase() != "play mo"
          : ans
      })
      return ans === undefined
    })
  }

  @computed
  get membersNeedingWaiver(): Member[] {
    return this.members.filter(m => m.waivers.length == 0)
  }

  @computed
  get minorsNeedingWaiver(): Member[] {
    const a = this.membersNeedingWaiver.filter(m => m.isMinor)
    return a
  }

  @computed
  get adultsNeedingWaiver(): Member[] {
    return this.membersNeedingWaiver.filter(m => !m.isMinor)
  }

  @computed
  get memberOwner(): Member | null {
    if (this.object.$memberOwner)
      return new Member(this.db, this.db.file.objects[
        this.object.$memberOwner
      ] as $Member)
    return null
  }

  @observable
  sqLocationId = "JTDNQA38SAMQS"

  @observable
  currentOrder: Order

  @computed
  get waiversNeedingSigned(): [Member, Waiver][] {
    const found: [Member, Waiver][] = []
    const arrayToUse =
      this.minorsNeedingWaiver.length > 0
        ? this.allAdults
        : this.adultsNeedingWaiver

    arrayToUse.forEach(m => {
      const waiver: Waiver = new Waiver(this.db, undefined, m.id)
      if (m && waiver) found.push([m, waiver])
    })
    return found
  }

  @action.bound
  addMember(member: Member) {
    if (member.isValid) {
      this.object.$members[member.id] = member.id
      member.minors.forEach(this.addMember)
    } else {
      throw `Cannot add invalid member to booking.  Member: ${
        member.fullName
      } ${member.id} has invalidMessage: ${member.invalidMessage}`
    }
  }

  @action.bound
  async emailOrderToCustomer(order: Order) {
    const postUrl = `${lightDBHost()}/send`
    const body = JSON.stringify({
      arrivingDate: this.arrivingDate.format("MMM DD, YYYY"),
      currentDate: dayjs().format("MMM DD, YYYY"),
      lineItems: order.lineItems.map(item => item.object),
      tax: order.taxFriendly,
      total: order.totalFriendly,
      customer: this.memberOwner && this.memberOwner.object,
      transaction_id: order.object.sqOrderId,
      transaction: order.transaction
    })
    await fetch(postUrl, {
      headers: new Headers({
        "Content-Type": "application/json"
      }),
      method: "POST",
      body: body,
      mode: "cors"
    })
      .then(res => res.json())
      .then(data => {
        console.log("Request succeeded with JSON response", data)
      })
      .catch(function(error) {
        console.log("Request failed", error)
      })
  }

  @action.bound
  async payCurrentOrder(cardNonce: string) {
    const order = this.currentOrder
    if (!this.memberOwner) {
      throw "Cannot pay because member owner is not set"
    }
    this.db.save(order)
    const note = `${this.memberOwner.fullName} ${dayjs(
      this.arrivingDate
    ).format("MMM DD")} via Dotcom Booking`
    await order.pay(cardNonce, note)
    await this.db.postNextSyncRequest()
    await this.emailOrderToCustomer(order)
    setTimeout(() => {
      this.currentOrder = new Order(
        this.db,
        this.id,
        "booking",
        this.sqLocationId
      )
    }, 3000)
  }
}
